import { CCol, CFormLabel } from '@coreui/react'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import type { DropdownChoice, DropdownChoiceId } from '@companion-app/shared/Model/Common.js'
import {
	type ButtonGraphicsElementBase,
	type SomeButtonGraphicsElement,
} from '@companion-app/shared/Model/StyleLayersModel.js'
import { ButtonGraphicsElementUsage } from '@companion-app/shared/Model/StyleModel.js'
import { DropdownInputField } from '~/Components/DropdownInputField.js'
import { InlineHelpIcon } from '~/Components/InlineHelp.js'
import { TextInputField } from '~/Components/TextInputField.js'
import { trpc, useMutationExt } from '~/Resources/TRPC.js'
import { useElementPropertiesContext } from './useElementPropertiesContext.js'

export const ElementCommonProperties = observer(function ElementCommonProperties({
	elementProps,
	simpleMode,
}: {
	elementProps: Readonly<SomeButtonGraphicsElement>
	simpleMode: boolean
}) {
	return (
		<>
			<CFormLabel htmlFor="inputName" className="col-sm-4 col-form-label col-form-label-sm">
				Element Name
			</CFormLabel>
			<CCol sm={8}>
				<FieldElementNameInput elementProps={elementProps} />
			</CCol>

			{elementProps.type !== 'canvas' && elementProps.type !== 'group' && !simpleMode && (
				<>
					<CFormLabel htmlFor="inputUsage" className="col-sm-4 col-form-label col-form-label-sm">
						External Usage
						<InlineHelpIcon className="ms-1">
							Some surfaces do not have full rgb displays and require specific elements for providing feedback in
							alternate ways.
							<br />
							You can override the automatic selection of elements for these purposes by selecting the appropriate usage
							for this element
						</InlineHelpIcon>
					</CFormLabel>
					<CCol sm={8}>
						<FieldElementUsageInput elementProps={elementProps} />
					</CCol>
				</>
			)}
		</>
	)
})

const FieldElementNameInput = observer(function FieldElementNameInput({
	elementProps,
}: {
	elementProps: SomeButtonGraphicsElement
}) {
	const { controlId } = useElementPropertiesContext()
	const setElementNameMutation = useMutationExt(trpc.controls.styles.setElementName.mutationOptions())

	const setName = useCallback(
		(value: string) => {
			setElementNameMutation
				.mutateAsync({ controlId, elementId: elementProps.id, name: value })
				.then((res) => {
					console.log('Update element', res)
				})
				.catch((e) => {
					console.error('Failed to Update element', e)
				})
		},
		[setElementNameMutation, controlId, elementProps.id]
	)

	return <TextInputField setValue={setName} value={elementProps.name ?? ''} />
})

const FieldElementUsageInput = observer(function FieldElementUsageInput({
	elementProps,
}: {
	elementProps: ButtonGraphicsElementBase
}) {
	const { controlId } = useElementPropertiesContext()
	const setElementUsageMutation = useMutationExt(trpc.controls.styles.setElementUsage.mutationOptions())

	const setUsage = useCallback(
		(value: DropdownChoiceId) => {
			setElementUsageMutation
				.mutateAsync({
					controlId,
					elementId: elementProps.id,
					usage: value as ButtonGraphicsElementUsage,
				})
				.then((res) => {
					console.log('Update element', res)
				})
				.catch((e) => {
					console.error('Failed to Update element', e)
				})
		},
		[setElementUsageMutation, controlId, elementProps.id]
	)

	// TODO: Should the choices be dynamic based on the element type?
	return <DropdownInputField setValue={setUsage} value={elementProps.usage} choices={elementUsageChoices} />
})

const elementUsageChoices: DropdownChoice[] = [
	{ id: ButtonGraphicsElementUsage.Automatic, label: 'Automatic' },
	{ id: ButtonGraphicsElementUsage.Text, label: 'Text' },
	{ id: ButtonGraphicsElementUsage.Color, label: 'Color' },
	{ id: ButtonGraphicsElementUsage.Image, label: 'Image' },
]
